'use client';

import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Dialog, Input, PulseRadio } from 'src/components';
import { useCreateProviderCohortMutation } from 'src/api/redux/providerCohorts';
import { useCreateFilterSetMutation } from 'src/api/redux/filterSets';
import { useNotification } from 'src/context/notification';
import { VolumeType, SavedType } from 'src/context/reducers/encountersReducer';
import { buildCohortLanguage, mapContextToCodeType } from 'src/components/Encounters/utils';

interface SaveNewVolumeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  volumeType: VolumeType;
  currentName: string;
  currentType: SavedType;
  billingCodes: any; // The current billing codes state
  onSaveSuccess?: () => void;
  originalState?: {
    newCohortName: string;
    selectedType: SavedType;
    billingCodes: any;
  };
}

const SaveNewVolumeDialog: React.FC<SaveNewVolumeDialogProps> = ({
  isOpen,
  onClose,
  volumeType,
  currentName,
  currentType,
  billingCodes,
  onSaveSuccess,
  originalState,
}) => {
  const [newName, setNewName] = useState('');
  const [selectedType, setSelectedType] = useState<SavedType>(
    SavedType.PERSONAL
  );
  const [isLoading, setIsLoading] = useState(false);
  const { setNotification } = useNotification();
  const [createCohort] = useCreateProviderCohortMutation();
  const [createFilterSet] = useCreateFilterSetMutation();

  // Initialize form values when the dialog opens
  useEffect(() => {
    if (isOpen) {
      setNewName(currentName);
      setSelectedType(currentType);
    }
  }, [isOpen, currentName, currentType]);

  // Get original values from originalState or fall back to current values
  const originalName = originalState?.newCohortName || currentName;
  const originalType = originalState?.selectedType || currentType;

  // Determine if save button should be disabled
  const isSaveDisabled =
    !newName.trim() || // Empty name
    (newName === originalName && selectedType === originalType) || // No changes from original
    isLoading; // Currently loading

  // Handler for radio button changes
  const handleTypeChange = (type: SavedType) => {
    setSelectedType(type);
  };

  // Function to build provider cohort request body
  const buildProviderCohortRequestBody = () => ({
    label: newName,
    scope: selectedType,
    cohort_language: buildCohortLanguage(billingCodes),
  });

  // Function to build filter set request body
  const buildFilterSetRequestBody = () => {
    // Group all codes by their type
    const codesByType: Record<string, string[]> = {};

    // Iterate through all groups and their codes
    billingCodes.groups.forEach((group: any) => {
      group.codes.forEach((code: any) => {
        const normalizedType = mapContextToCodeType(code.context_type);
        if (!codesByType[normalizedType]) {
          codesByType[normalizedType] = [];
        }
        codesByType[normalizedType].push(code.id);
      });
    });

    // Convert the grouped codes into the filters array format
    const filters = Object.entries(codesByType).map(([type, values]) => ({
      type,
      is_exclude: billingCodes.groups[0].inverse, // Use the first group's inverse value
      is_all: false,
      values,
    }));

    // Construct the final request body
    return {
      label: newName,
      type: 'CODE',
      scope: selectedType,
      filters,
    };
  };

  const handleSave = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsLoading(true);

    try {
      if (volumeType === VolumeType.ENCOUNTERS) {
        // Save as new provider cohort
        const requestBody = buildProviderCohortRequestBody();
        const response = await createCohort(requestBody).unwrap();

        // No manual refresh needed - RTK Query cache auto-invalidates

        setNotification({
          title: 'Success',
          message: `Encounter Cohort "${response?.label}" created successfully`,
          type: 'success',
        });
      } else {
        // Save as new filter set using RTK Query mutation
        // Cache invalidation happens automatically via invalidatesTags
        const requestBody = buildFilterSetRequestBody();
        const response = await createFilterSet({ data: requestBody }).unwrap();

        setNotification({
          title: 'Success',
          message: `Code Set "${response?.label}" created successfully`,
          type: 'success',
        });
      }

      // Call the onSaveSuccess callback if provided
      if (onSaveSuccess) {
        onSaveSuccess();
      }
    } catch (error: any) {
      console.error('Error creating volume:', error);

      setNotification({
        title: 'Error',
        message:
          error?.data?.error_message ||
          error?.error_message ||
          'Failed to create volume. Please try again.',
        type: 'error',
      });
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  const dialogTitle = 'Save As New Volume';

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      aria-labelledby="save-new-volume-dialog"
      size="sm"
      hideBackdrop={false}
      className="!h-auto"
      showCloseButton
    >
      <Dialog.Content>
        <Box className="p-6" onClick={(e) => e.stopPropagation()}>
          <Typography
            className="text-neutral-800 font-semibold mb-4"
            data-testid="save-volume-dialog-title"
          >
            {dialogTitle}
          </Typography>

          {/* Volume Name Input */}
          <Box className="py-4">
            <Typography className="text-sm font-semibold mb-2">
              Volume Name
            </Typography>
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              placeholder="Type a name..."
              className="w-full rounded-full border-neutral-300 focus-within:border-sky-600 focus-within:border-1 focus-within:shadow-[0px_0px_2px_2px_theme(colors.sky.100)]"
              data-testid="save-volume-name-input"
              variant="outlined"
              size="md"
              fullWidth
            />
          </Box>

          {/* Volume Type Radio Buttons - styled like SaveVolumeType */}
          <Box className="py-1">
            <Box className="inline-flex justify-start items-start gap-4">
              <PulseRadio
                variant="solid"
                checked={selectedType === SavedType.PERSONAL}
                onChange={() => handleTypeChange(SavedType.PERSONAL)}
                onClick={(e) => e.stopPropagation()}
                className="flex justify-start items-center gap-2"
                label="Personal"
                data-testid="save-volume-personal-radio"
              />
              <PulseRadio
                variant="solid"
                checked={selectedType === SavedType.COMPANY}
                onChange={() => handleTypeChange(SavedType.COMPANY)}
                onClick={(e) => e.stopPropagation()}
                className="flex justify-start items-center gap-2"
                label="Company"
                data-testid="save-volume-company-radio"
              />
            </Box>
          </Box>

          {/* Buttons */}
          <Box className="flex justify-end gap-3 pt-3">
            <Button
              variant="outlined"
              color="neutral"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClose();
              }}
              className="px-4"
              data-testid="save-volume-dialog-cancel-button"
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="solid"
              color="primary"
              onClick={handleSave}
              className="px-4"
              data-testid="save-volume-dialog-save-button"
              loading={isLoading}
              disabled={isSaveDisabled}
            >
              Save Volume
            </Button>
          </Box>
        </Box>
      </Dialog.Content>
    </Dialog>
  );
};

export default SaveNewVolumeDialog;
